import { Action } from 'redux';
import {
    EditorColor,
    EditorSaveData,
    EditorSaveError,
    EditorTemplateStatus,
    EditorTemplateVersion,
} from '@dev/template-editor';
import { Form, FormPayload, ListPayload, Template, PreviewPageType } from '@dev/ee-api/ee-api-models';
import { AvailableFeaturesUpselling } from '@dev/ee-modules';
import { Step } from '@/components/FormCreator/types';

export enum FormCreatorStatus {
    DEFAULT = 'default',
    LOADING = 'loading',
    LOADED = 'loaded',
    SAVING = 'saving',
    SAVED = 'saved',
    LOAD_ERROR = 'load-error',
    SAVE_ERROR = 'save-error'
}

export type FormErrors = Partial<Record<FormErrorNames, boolean>>;
export enum FormErrorNames {
    DUPLICATED_NAME = 'DuplicatedName',
    NOTIFY_EMAILS_NEMBER_EXCEEDED = 'EmailToNotifyNumberExceeded',
    NOTIFY_EMAILS_NO_PROPER_SEPARATOR = 'EmailToNotifyNotSeparatedBySemicolon',
    NOTIFY_INVALID_EMAILS = 'EmailsToNotifyInvalid',
    NOTIFY_EMAILS_NOT_ON_CONTACT_LIST = 'EmailsToNotifycNotOnContactList',
    ACTIVATION_VIA_EMAIL_INVALID_URL = 'ActivationViaEmailInvalidUrl',
    FORM_SUBMIT_INVALID_URL = 'FormSubmitInvalidUrl',
    ALREADY_SUBSCRIBED_INVALID_URL = 'AlreadySubscribedInvalidUrl',
}

export enum FormPopupTriggerEnum {
    Delay = 'delay',
}

export interface EditorMetaData {
    data: {
        contentJson: EditorTemplateVersion;
        recentColors: EditorColor[];
    };
}
export interface FormMetaData {
    data: {
        popupTrigger: FormPopupTriggerEnum;
        popupDelay?: number;
    };
}

export type SendTestErrors = Record<string, boolean>;

export enum EditorMediaManagerTabIndex {
    IMAGE = 0,
    FILE = 1,
}

export type EditorMediaManagerProps = {
    showImageTabOnly: boolean;
    isLinkToFile: boolean;
    blockId: string;
    isOpen: boolean;
};

export type EditorFile = {
    fileUrl: string;
    fileName: string;
    blockId: string;
    isLinkToFile?: boolean;
};

export type FilesType = {
    filesList: EditorFile[];
    isFileUploading: boolean;
};

export type TemplatePreviewType = {
    templateHtml: string;
};

export interface LoadStartAction extends Action {
    type: FormCreatorActionTypes.LOAD_START;
    payload: {
        name: string;
    };
}
export interface LoadSuccessAction extends Action {
    type: FormCreatorActionTypes.LOAD_SUCCESS;
    payload: {
        formObject: Form;
    };
}

export interface LoadErrorAction extends Action {
    type: FormCreatorActionTypes.LOAD_ERROR;
}

export interface SaveStartAction extends Action {
    type: FormCreatorActionTypes.SAVE_START;
    payload: {
        isPopupCreator?: boolean;
    }
}
export interface SaveSuccessAction extends Action {
    type: FormCreatorActionTypes.SAVE_SUCCESS;
    payload: {
        formObject: Form;
    }
}

export interface SaveErrorAction extends Action {
    type: FormCreatorActionTypes.SAVE_ERROR;
}

export interface SetFormErrorsAction extends Action {
    type: FormCreatorActionTypes.SET_FORM_ERRORS;
    payload: {
        errors: FormErrors;
    }
}

export interface ModifyFormAction extends Action {
    type: FormCreatorActionTypes.MODIFY_FORM;
    payload: {
        formPayload: Partial<FormPayload>;
    }
}

export interface StartEditStepAction extends Action {
    type: FormCreatorActionTypes.START_EDIT_STEP;
    payload: {
        stepInEdit: Step;
    }
}

export interface CancelEditStepAction extends Action {
    type: FormCreatorActionTypes.CANCEL_EDIT_STEP;
}

export interface CloseAction extends Action {
    type: FormCreatorActionTypes.CLOSE;
}

export interface TemplateUploadFileAction extends Action {
    type: FormCreatorActionTypes.TEMPLATE_UPLOAD_FILE;
    payload: {
        file: File;
        blockId: string;
        isLinkToFile?: boolean;
    };
}

export interface TemplateUploadFileStartAction extends Action {
    type: FormCreatorActionTypes.TEMPLATE_UPLOAD_FILE_START;
}

export interface TemplateUploadFileSuccessAction extends Action {
    type: FormCreatorActionTypes.TEMPLATE_UPLOAD_FILE_SUCCESS;
    payload: {
        filesList: EditorFile[];
    };
}

export interface TemplateUploadFileErrorAction extends Action {
    type: FormCreatorActionTypes.TEMPLATE_UPLOAD_FILE_ERROR;
}

export interface SetDefaultTemplateAction extends Action {
    type: FormCreatorActionTypes.SET_DEFAULT_TEMPLATE;
    payload: {
        templateObject: Template;
    };
}

export interface TemplateLoadStartAction extends Action {
    type: FormCreatorActionTypes.TEMPLATE_LOAD_START;
    payload: {
        name?: string;
        isPublic: boolean;
        useDraftScope: boolean;
    };
}

export interface TemplateLoadSuccessAction extends Action {
    type: FormCreatorActionTypes.TEMPLATE_LOAD_SUCCESS;
    payload: {
        templateObject: Template;
        isPublic: boolean;
        useDraftScope: boolean;
    };
}

export interface TemplateLoadErrorAction extends Action {
    type: FormCreatorActionTypes.TEMPLATE_LOAD_ERROR;
}

export interface TemplateIncrementAutosaveCounterAction extends Action {
    type: FormCreatorActionTypes.TEMPLATE_INCREMENT_AUTOSAVE_COUNTER;
}

export interface TemplateSaveStartAction extends Action {
    type: FormCreatorActionTypes.TEMPLATE_SAVE_START;
    payload: {
        data?: EditorSaveData;
        exitOnSave?: boolean;
        errors?: EditorSaveError[];
    };
    meta: {
        thunk: boolean;
    };
}

export interface TemplateSaveSuccessAction extends Action {
    type: FormCreatorActionTypes.TEMPLATE_SAVE_SUCCESS;
    payload: {
        data: EditorSaveData;
    };
}

export interface TemplateSaveErrorAction extends Action {
    type: FormCreatorActionTypes.TEMPLATE_SAVE_ERROR;
    payload: {
        nameError?: string;
    };
}

export interface OpenUpsellingOverlayAction extends Action {
    type: FormCreatorActionTypes.OPEN_UPSELLING_OVERLAY;
    payload: {
        feature: AvailableFeaturesUpselling;
    };
}
export interface CloseUpsellingOverlayAction extends Action {
    type: FormCreatorActionTypes.CLOSE_UPSELLING_OVERLAY;
}

export interface SetIsTemplateOpenAction extends Action {
    type: FormCreatorActionTypes.SET_IS_TEMPLATE_OPEN;
    payload: {
        isOpen: boolean;
    };
    meta: Record<string, unknown>;
}

export interface SetTemplatePreviewAction extends Action {
    type: FormCreatorActionTypes.SET_TEMPLATE_PREVIEW;
    payload: {
        content?: string;
        name?: string;
        isPublic?: boolean;
    }
}

export interface ResetAction extends Action {
    type: FormCreatorActionTypes.RESET;
}

export interface TemplateShowSaveErrorsAction extends Action {
    type: FormCreatorActionTypes.TEMPLATE_SHOW_SAVE_ERRORS;
}

export interface TemplateChangedAction extends Action {
    type: FormCreatorActionTypes.TEMPLATE_CHANGED;
}

export interface TemplateDeselectAction extends Action {
    type: FormCreatorActionTypes.TEMPLATE_DESELECT;
}

export interface UpdateContactListsAction extends Action {
    type: FormCreatorActionTypes.UPDATE_CONTACT_LISTS;
    payload: {
        contactLists: string[];
    };
}

export interface AddContactListStartAction extends Action {
    type: FormCreatorActionTypes.ADD_CONTACT_LIST_START;
    payload: {
        list: ListPayload;
    };
}

export interface AddContactListErrorStatusAction extends Action {
    type: FormCreatorActionTypes.ADD_CONTACT_LIST_ERROR_STATUS;
    payload: {
        isAddListError: boolean;
    };
}

export interface LoadPageTemplateStartAction extends Action {
    type: FormCreatorActionTypes.LOAD_PAGE_TEMPLATE_START;
    payload: {
        pageTemplateType: PreviewPageType;
    }
}
export interface LoadPageTemplateSuccessAction extends Action {
    type: FormCreatorActionTypes.LOAD_PAGE_TEMPLATE_SUCCESS;
    payload: {
        pageTemplateType: PreviewPageType;
        pageTemplate: string;
    }
}

export interface LoadPageTemplateErrorAction extends Action {
    type: FormCreatorActionTypes.LOAD_PAGE_TEMPLATE_ERROR;
}

export interface OpenGalleryAction extends Action {
    type: FormCreatorActionTypes.OPEN_GALLERY;
}

export interface CloseGalleryAction extends Action {
    type: FormCreatorActionTypes.CLOSE_GALLERY;
}

export interface TemplatePreviewFromGalleryAction extends Action {
    type: FormCreatorActionTypes.TEMPLATE_PREVIEW_FROM_GALLERY;
    payload: {
        templateName?: string;
        isPublic?: boolean;
    };
}
export interface TemplateOpenFromGalleryAction extends Action {
    type: FormCreatorActionTypes.TEMPLATE_OPEN_FROM_GALLERY;
    payload: {
        templateName?: string;
        isPublic?: boolean;
    };
}

export interface SetPopupDelayAction extends Action {
    type: FormCreatorActionTypes.SET_POPUP_DELAY;
    payload: {
        popupDelay: number;
    };
}
export interface SetPopupTriggerAction extends Action {
    type: FormCreatorActionTypes.SET_POPUP_TRIGGER;
    payload: {
        popupTrigger: FormPopupTriggerEnum;
    };
}

export interface UpdateSaveSettingsAction extends Action {
    type: FormCreatorActionTypes.UPDATE_SAVE_SETTINGS;
    payload: SaveSettings;
}

export interface FormCreatorTemplate {
    name: string,
    loadedJson?: EditorTemplateVersion;
    status: EditorTemplateStatus;
    recentColors: EditorColor[];
    autosaveCounter: number;
    isNew?: boolean;
    files: FilesType;
    nameError?: string;
    saveErrors?: EditorSaveError[],
}

export interface SaveSettings {
    isGalleryCreator?: boolean;
    isPublic?: boolean;
    customScreenshot?: string;
    isDismissButtonVisible?: boolean;
}
export interface FormCreatorState {
    status: FormCreatorStatus;
    isTemplateOpen: boolean;
    isTemplateDeselected: boolean;
    previousTemplate?: FormCreatorTemplate;
    template: FormCreatorTemplate;
    formObject?: Form;
    formPayload?: FormPayload;
    pageTemplates?: {
        activationViaEmailPage: string;
        alreadySubscribedPage: string;
        formSubmitPage: string;
        formSubmitDoubleOptInPage: string;
        landingPage: string;
    };
    nameError?: string;
    openUpsellingFeature?: AvailableFeaturesUpselling;
    contactLists?: string[];
    stepInEdit?: Step;
    isAddListError?: boolean;
    templatePreview?: {
        name?: string;
        isPublic?: boolean;
        content?: string;
    };
    isGalleryOpen: boolean;
    formEditor: {
        isOpen?: boolean;
        sourceTemplateName?: string;
    };
    errors?: FormErrors;
    metaData?: FormMetaData;
    popupTrigger?: FormPopupTriggerEnum;
    popupDelay?: number;
    saveSettings: SaveSettings;
}

export enum FormCreatorActionTypes {
    CLOSE = '@@formCreator/CLOSE',
    RESET = '@@formCreator/RESET',

    LOAD_START = '@@formCreator/LOAD_START',
    LOAD_SUCCESS = '@@formCreator/LOAD_SUCCESS',
    LOAD_ERROR = '@@formCreator/LOAD_ERROR',

    SAVE_START = '@@formCreator/SAVE_START',
    SAVE_SUCCESS = '@@formCreator/SAVE_SUCCESS',
    SAVE_ERROR = '@@formCreator/SAVE_ERROR',

    START_EDIT_STEP = '@@formCreator/START_EDIT_STEP',
    CANCEL_EDIT_STEP = '@@formCreator/CANCEL_EDIT_STEP',
    MODIFY_FORM = '@@formCreator/MODIFY_FORM',

    OPEN_UPSELLING_OVERLAY = '@@formCreator/OPEN_UPSELLING_OVERLAY',
    CLOSE_UPSELLING_OVERLAY = '@@formCreator/CLOSE_UPSELLING_OVERLAY',

    SET_IS_TEMPLATE_OPEN = '@@formCreator/SET_IS_TEMPLATE_OPEN',
    SET_DEFAULT_TEMPLATE = '@@formCreator/SET_DEFAULT_TEMPLATE',

    TEMPLATE_SET_NAME = '@@formCreator/TEMPLATE_SET_NAME',
    TEMPLATE_SET_NAME_ERROR = '@@formCreator/TEMPLATE_SET_NAME_ERROR',
    TEMPLATE_INCREMENT_AUTOSAVE_COUNTER = '@@formCreator/TEMPLATE_INCREMENT_AUTOSAVE_COUNTER',
    TEMPLATE_CHANGED = '@@formCreator/TEMPLATE_CHANGED',
    TEMPLATE_LOAD_START = '@@formCreator/TEMPLATE_LOAD_START',
    TEMPLATE_LOAD_SUCCESS = '@@formCreator/TEMPLATE_LOAD_SUCCESS',
    TEMPLATE_LOAD_ERROR = '@@formCreator/TEMPLATE_LOAD_ERROR',
    TEMPLATE_SAVE_START = '@@formCreator/TEMPLATE_SAVE_START',
    TEMPLATE_SAVE_SUCCESS = '@@formCreator/TEMPLATE_SAVE_SUCCESS',
    TEMPLATE_SAVE_ERROR = '@@formCreator/TEMPLATE_SAVE_ERROR',
    TEMPLATE_SHOW_SAVE_ERRORS = '@@formCreator/TEMPLATE_SHOW_SAVE_ERRORS',
    TEMPLATE_DESELECT = '@@formCreator/TEMPLATE_DESELECT',
    TEMPLATE_OPEN_FROM_GALLERY = '@@formCreator/TEMPLATE_OPEN_FROM_GALLERY',
    TEMPLATE_PREVIEW_FROM_GALLERY = '@@formCreator/TEMPLATE_PREVIEW_FROM_GALLERY',

    SET_TEMPLATE_PREVIEW = '@@formCreator/SET_TEMPLATE_PREVIEW',
    TEMPLATE_UPLOAD_FILE = '@@formCreator/TEMPLATE_UPLOAD_FILE',
    TEMPLATE_UPLOAD_FILE_START = '@@formCreator/TEMPLATE_UPLOAD_FILE_START',
    TEMPLATE_UPLOAD_FILE_SUCCESS = '@@formCreator/TEMPLATE_UPLOAD_FILE_SUCCESS',
    TEMPLATE_UPLOAD_FILE_ERROR = '@@formCreator/TEMPLATE_UPLOAD_FILE_ERROR',

    UPDATE_CONTACT_LISTS = '@@formCreator/CONTACT_LISTS_UPDATE',
    ADD_CONTACT_LIST_START = '@@formCreator/ADD_CONTACT_LIST_START',
    ADD_CONTACT_LIST_ERROR_STATUS = '@@formCreator/ADD_CONTACT_LIST_ERROR_STATUS',

    LOAD_PAGE_TEMPLATE_START = '@@formCreator/LOAD_PAGE_TEMPLATE_START',
    LOAD_PAGE_TEMPLATE_SUCCESS = '@@formCreator/LOAD_PAGE_TEMPLATE_SUCCESS',
    LOAD_PAGE_TEMPLATE_ERROR = '@@formCreator/LOAD_PAGE_TEMPLATE_ERROR',

    OPEN_GALLERY = '@@formCreator/OPEN_GALLERY',
    CLOSE_GALLERY = '@@formCreator/CLOSE_GALLERY',

    SET_FORM_ERRORS = '@@formCreator/SET_FORM_ERRORS',
    SET_POPUP_DELAY = '@@formCreator/SET_POPUP_DELAY',
    SET_POPUP_TRIGGER = '@@formCreator/SET_POPUP_TRIGGER',
    UPDATE_SAVE_SETTINGS = '@@formCreator/UPDATE_SAVE_SETTINGS',
}
